import { useEffect, useLayoutEffect, useState } from "react";

import {
  AppBar,
  Box,
  List,
  ListItem,
  Skeleton,
  Toolbar,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";

import HelpMenu from "../help/HelpMenu";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import BrandMark from "../components/BrandMark";
import { getBrand, getInstallName } from "../config/product";
import MenuPopupState from "./Menu";
import MenuDrawer from "./MenuDrawer";
import CustomSignOut from "./SignOut";

import type { NavProps } from "./types";
import MyMdHelpMenu from "../help/MyMdHelpMenu";
import NotificationsMenu from "./NotificationsMenu";
import { TOP_BAR_ICON_COLOR } from "../components/dropdownMenuStyles";

/** Same height as the Brain wordmark (26px) — tenant image replaces that mark. */
const LOGO_HEIGHT = 26;

const Nav: React.FC<React.PropsWithChildren<NavProps>> = ({
  children,
  nav,
}) => {
  const globalConfig = useGlobalConfig();
  const [domainName, setDomainName] = useState("");
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState<string>();

  const [activeNavItem, setActiveNavItem] = useState<string | null>(() => {
    try {
      return localStorage.getItem("activeNavItem");
    } catch (e) {
      return null;
    }
  });

  const setActiveNavItemPersisted = (id: string | null) => {
    setActiveNavItem(id);
    try {
      if (id === null) localStorage.removeItem("activeNavItem");
      else localStorage.setItem("activeNavItem", id);
    } catch (e) {
      // ignore storage errors
    }
  };

  const brand = getBrand();

  useLayoutEffect(() => {
    if (brand.logo.kind === "asset") {
      setLogoUrl((process.env.PUBLIC_URL || "") + "/" + brand.logo.file);
    }
    if (globalConfig?.site_logo) {
      let env = "";
      if (process.env.REACT_APP_ENV !== "prod") {
        env = "_" + process.env.REACT_APP_ENV;
      }
      setLogoUrl(
        globalConfig.site_domain +
          globalConfig.site_path +
          "data/module_" +
          getInstallName() +
          env +
          "/" +
          globalConfig.site_logo
      );
    }
  }, [globalConfig, brand]);

  useEffect(() => {
    const url =window.location.href
    const removeHttps = url.split("//");
    const splitUrl = removeHttps[1].split(".")
   
    if (splitUrl.length>0)
    {
      if (splitUrl[0].includes("mymd"))
      {
        setDomainName('mymd')
      }
      else{
        if (splitUrl.length === 3){
          setDomainName(splitUrl[1])
        }
        else if(splitUrl.length === 2){
          setDomainName(splitUrl[0])
        }
       
      }
    }
   
   
  }, []);

  useEffect(() => {
    if (!nav) return;

    const path = location.pathname || "";

    if (path.startsWith("/app") || path.startsWith("/aeronet5")) {
      const aeronet5 = nav.find((n: any) => n.name === "Aeronet 5.0" || n.id === "aeronet5");
      if (aeronet5) {
        setActiveNavItemPersisted(aeronet5.id);
        return;
      }
    }
  }, [location.pathname, nav]);

  return (
    <>
      <AppBar
        position="relative"
        sx={{
          backgroundColor: "black",
          // Match Aeronet Brain top bar (`layout.topBarHeight`)
          height: 70,
          justifyContent: "center",
        }}
        elevation={0}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: "70px !important",
            height: 70,
            alignItems: "center",
          }}
        >
          <>
            <MenuDrawer nav={nav}></MenuDrawer>
            <Box
              display="flex"
              flexGrow="1"
              sx={{
                alignItems: "center",
                transform: "translateY(2px)",
                paddingLeft: { sm: -1.5 },
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  height: LOGO_HEIGHT,
                  minHeight: LOGO_HEIGHT,
                  maxWidth: "100%",
                }}
              >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  flexShrink: 0,
                  height: LOGO_HEIGHT,
                }}
              >
                {brand.logo.kind === "svg" ? (
                  <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                    <BrandMark size={24} color="#fff" />
                  </Link>
                ) : logoUrl ? (
                  <Link to="/" style={{ display: "flex", alignItems: "flex-end" }}>
                    <img
                      src={logoUrl}
                      alt="logo"
                      // A tenant's config-driven site_logo can point at a legacy
                      // install URL that 404s on this plane — never render a
                      // broken image; fall back to the brand asset.
                      onError={() => {
                        const fallback =
                          (process.env.PUBLIC_URL || "") +
                          "/" +
                          (brand.logo.kind === "asset" ? brand.logo.file : "aeronet_banner_2022.png");
                        if (logoUrl !== fallback) setLogoUrl(fallback);
                      }}
                      style={{
                        height: LOGO_HEIGHT,
                        width: "auto",
                        maxWidth: 148,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Link>
                ) : (
                  <Skeleton
                    animation="wave"
                    variant="rounded"
                    sx={{ bgcolor: "grey.900" }}
                    width={148}
                    height={LOGO_HEIGHT}
                  />
                )}
              </Box>

              <List
                component="nav"
                sx={{
                  display: { xs: "none", md: "flex" },
                  flexDirection: "row",
                  alignItems: "flex-end",
                  padding: 0,
                  marginLeft: 5,
                  maxWidth: "100%",
                  flexWrap: "nowrap",
                  height: LOGO_HEIGHT,
                }}
              >
                {nav && nav.length > 0
                  ? nav
                      .map((navItem, index) => (
                      <ListItem
                        key={navItem.id}
                        value={navItem.id}
                        sx={{
                          whiteSpace: "nowrap",
                          paddingTop: 0,
                          paddingBottom: 0,
                          width: "auto",
                          minHeight: "0 !important",
                          height: "auto",
                          alignItems: "flex-end",
                          pb: "3px",
                        }}
                      >
                        <MenuPopupState navItem={navItem} key={index} activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItemPersisted} />
                      </ListItem>
                    ))
                  : Array(5)
                      .fill(1)
                      .map((_, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            whiteSpace: "nowrap",
                            paddingTop: 0,
                            paddingBottom: 0,
                            width: "auto",
                            minHeight: "0 !important",
                          }}
                        >
                          <Skeleton
                            key={index}
                            variant="text"
                            animation="wave"
                            sx={{ fontSize: "2rem", bgcolor: "grey.900" }}
                            width={140}
                          />
                        </ListItem>
                      ))}
              </List>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={0.5}
              sx={{
                color: TOP_BAR_ICON_COLOR,
              }}
            >
              <NotificationsMenu />
              {
                domainName === "mymd"?<MyMdHelpMenu/>:<HelpMenu />
              }
              <CustomSignOut />
            </Box>
          </>
        </Toolbar>
      </AppBar>
      {children}
    </>
  );
};

export default Nav;
